// A panel that tells the truth about its own state.
//
// Every Operations surface is a set of panels over endpoints that can be
// loading, empty, refused or broken. Rendering all four honestly is the whole
// job — a panel that shows nothing when the server refused it is indistinguishable
// from a panel showing that nothing is happening, and on an operations screen
// those two mean opposite things.
//
// STYLING USES THE SHARED DESIGN SYSTEM (J1)
// ===========================================
// Reaches for `card`, `dim`, `section-title`, `state` and `skeleton` from
// `cipher-system.css` (the `.cipher-ui`-scoped design system — see
// DESIGN-SESSION-STEP1C) rather than inventing parallel classes. The two
// container wrappers below (`Figure`, `Rows`) that no shared class covers get
// their own small `op-` rules in `./operations.css`, following the same
// area-owns-its-layout pattern as `workstation/workstation.css`.
import React from "react";

import EmptyState from "../../components/EmptyState";
import { FAILED, LOADING, READY, REFUSED } from "./useEndpoint";

export default function Panel({ title, state, empty, children, note }) {
  const { status, error } = state;

  return (
    <section className="card">
      <div className="section-title">{title}</div>

      {status === LOADING ? (
        <div className="skeleton" role="status" aria-label={`Loading ${title}`} />
      ) : null}

      {status === REFUSED ? (
        <EmptyState
          title="Not available to this account"
          detail={`The server refused this request (${error}). Navigation shows it; the data is gated where it should be.`}
        />
      ) : null}

      {status === FAILED ? (
        <EmptyState
          title="Could not load"
          detail={`${error}. This panel is reporting the failure rather than showing a blank that would read as calm.`}
        />
      ) : null}

      {status === READY && empty ? (
        <EmptyState title="Nothing here yet" detail={note} />
      ) : null}

      {status === READY && !empty ? children : null}
    </section>
  );
}

/** A labelled figure. Absent values render as "—", never as a fabricated 0.
 *  `tone` colours the number semantically; `detail` is the line beneath it. */
export function Figure({ label, value, tone, detail }) {
  const absent = value === "\u2014" || value === "-" || value == null;
  return (
    <div className="op-figure">
      <div className="op-figure-v" data-tone={absent ? "absent" : tone || ""}>{value}</div>
      <div className="op-figure-k">{label}</div>
      {detail ? <div className="op-figure-d">{detail}</div> : null}
    </div>
  );
}

/** Map whatever an endpoint calls "fine" onto one of four dot states.
 *  Anything unrecognised stays `unknown` rather than being read as healthy —
 *  on an operations screen, guessing green is the dangerous direction. */
export function tone(value) {
  const v = String(value == null ? "" : value).toLowerCase();
  if (!v || v === "—" || v === "unknown") return "unknown";
  if (["green", "ok", "up", "healthy", "running", "true", "closed", "active", "connected"].includes(v)) return "ok";
  if (["yellow", "degraded", "warn", "warning", "attention", "slow", "partial"].includes(v)) return "warn";
  if (["red", "down", "error", "failed", "unhealthy", "tripped", "false", "open"].includes(v)) return "crit";
  return "unknown";
}

/** A row whose left side carries a status dot derived from its own value. */
export function StatusRows({ items }) {
  return (
    <dl className="op-rows">
      {items.map(([k, v]) => (
        <div key={k} className="op-row">
          <dt><i className="op-dot" data-tone={tone(v)} />{k}</dt>
          <dd data-tone={tone(v)}>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

/** A plain definition row, for endpoint payloads that are flat objects. */
export function Rows({ items }) {
  return (
    <dl className="op-rows">
      {items.map(([k, v]) => (
        <div key={k} className="op-row">
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}
